import { addHours, addMinutes, addDays, format } from "date-fns";
import TableCell from "../components/Table/TableCell";
import MultipleRowsCell from "../components/Table/MultipleRowsCell";
import { createColumnHelper } from "@tanstack/react-table";
import { toast } from "react-toastify";
import { resendNewClientEmail } from "./utils";
import { toastAxios } from "./toastAxios";
import { ROLES } from "../constants/base";
import { getCurrencySymbol, getCurrencyCode } from "./currency";
import { getWineColor, getWineColorClassName } from "./wineColor";

// Additive product-addition operation kinds. The backend only recognizes the
// generic "ADD" type (env OPERATION_TYPES) — TANNISSAGE/ENZYME/SO2/NITROGEN/
// INOCULATION/BOIS exist only as winemaking-protocol step kinds and on legacy
// operation rows created via direct INSERT (protocolExecutor). The frontend
// routes them all under the /add URL segment and submits type "ADD".
const ADDITIVE_OP_TYPES = [
  "ADD",
  "ENZYME",
  "TANNISSAGE",
  "SO2",
  "NITROGEN",
  "INOCULATION",
  "BOIS",
];

// URL segment for an operation type: additive kinds collapse to "add",
// everything else keeps its own lowercased segment.
const getOperationUrlSegment = (type) =>
  ADDITIVE_OP_TYPES.includes(type?.toUpperCase?.())
    ? "add"
    : type?.toLowerCase?.();

// Backend-valid operation type for submit: additive kinds map to "ADD" (the
// only additive type in OPERATION_TYPES); everything else passes through.
const toBackendOperationType = (type) =>
  ADDITIVE_OP_TYPES.includes(type?.toUpperCase?.()) ? "ADD" : type;

// Builds the field values to populate into WineMakingProtocolModalChildren's form
// when opening it to edit an existing winemaking-protocol row. Shared by the two
// places that open this modal for an existing row:
// - Table/EditCell.js (setWineMakingRowData) - editing a template in WineMakingProtocolForm.js
// - Generic/index.js's DrugsBatchSelectorTable (handleOpenModal) - configuring a step
//   while applying a protocol in WineMakingForm.js
// Callers are responsible for actually calling their own setValue(key, value) for
// each entry returned here - this function only computes what to set.
//
// @param {Object} rowData - The row being edited (operationsP[idx] / row.original)
// @param {Object} opts
// @param {Function} opts.getLabel - i18n label getter
// @param {boolean} opts.isDayBasedProtocol - false for CIP (day = step number,
//   already 1-indexed), true otherwise (day is 0-indexed, shown to the user as +1)
// @param {Array} [opts.machineries] - winery machineries list, used to resolve
//   machinery_id -> name when rowData.machinery_name isn't already set (e.g. the
//   row was built locally by updateWinemakingTableData, which only stores the id)
// @returns {Object} Map of field name -> value to set on the modal's form
const buildWineMakingModalValues = (rowData, { getLabel, isDayBasedProtocol, machineries }) => {
  const values = {};

  values.id = rowData?.id;
  values.day = isDayBasedProtocol ? (Number(rowData?.day) || 0) + 1 : rowData?.day;

  values.phase = {
    label: getLabel(`protocolPhase_${rowData?.phase}`),
    value: rowData?.phase,
  };

  // Le operazioni additive legacy (TANNISSAGE/ENZYME/SO2/...) esistono solo come
  // step di protocollo: il tipo canonico nel form è "ADD" (unico additivo
  // selezionabile nella dialog e che attiva i campi functional/product/quantity).
  const normalizedOperation = toBackendOperationType(rowData?.operation);
  values.operation = {
    label: getLabel(`protocolOperation_${normalizedOperation}`),
    value: normalizedOperation,
  };

  if (Number(rowData?.day_to) > 0) {
    values.day_to = isDayBasedProtocol ? Number(rowData.day_to) + 1 : rowData?.day_to;
    values.interval = true;
    // RANGE = repeat every day in the window, ONCE_WITHIN = once, anywhere in the window
    values.repeat_daily = rowData?.timing_mode === "RANGE";
  } else {
    values.day_to = "";
    values.interval = false;
    values.repeat_daily = false;
  }

  values.machinery_id = rowData?.machinery_id
    ? {
        value: rowData.machinery_id,
        label:
          rowData?.machinery_name ||
          machineries?.find((m) => m.id === rowData.machinery_id)?.name ||
          "",
      }
    : { value: "", label: "" };

  if (rowData?.operation === "HOMOGENIZATION_MASS") {
    values.protocol_type = {
      label: getLabel(`protocolOperation_${rowData?.protocol_type}`),
      value: rowData?.protocol_type || "",
    };
  }

  if (
    !["OXYGENATION", "DECANT", "RACKING", "HOMOGENIZATION_MASS"].includes(
      rowData?.operation
    )
  ) {
    values.functional = {
      label: getLabel(`drugLabelGroup_${rowData?.functional}_One`),
      value: rowData?.functional,
    };

    const product = {
      ...rowData?.product,
      label: rowData?.product?.name,
      value: rowData?.product?.id,
      protocol_type: "ADD",
    };

    if (["SO2", "RINSE", "VAPORIZATION"].includes(rowData?.operation)) {
      const name = getLabel(`protocolProduct_${product?.name}`);
      product.label = name;
      product.name = name;
    }

    values.product = product;
    values.quantity = rowData?.quantity;
  }

  if (["RINSE", "CLEANING", "VAPORIZATION"].includes(rowData?.operation)) {
    values.work_time = rowData?.work_time;
  }

  if (rowData?.operation === "HOMOGENIZATION_MASS") {
    values.quantity = rowData?.quantity;
    values.protocol_type = {
      label: getLabel(`protocolType_${rowData?.protocol_type}`),
      value: rowData?.protocol_type,
    };

    if ("HOMOGENIZATION_REASSEMBLY" === rowData?.protocol_type) {
      values.homogenization_type = {
        label: getLabel(`protocolHomogenizationMass_${rowData?.homogenization_type}`),
        value: rowData?.homogenization_type,
      };
      values.work_time = rowData?.work_time;
      values.pause_time = rowData?.pause_time;
      values.motor_speed = rowData?.motor_speed;
      values.iter_per_day = rowData?.iter_per_day;
    }
  }

  if ("OXYGENATION" === rowData?.operation) {
    values.protocol_type = {
      label: getLabel(`protocolOXYGENATION_${rowData?.protocol_type}`),
      value: rowData?.protocol_type,
    };
    values.quantity = rowData?.quantity;
    if ("CUSTOM" === rowData?.protocol_type) {
      values.oxygenation_dose_hours = rowData?.oxygenation_dose_hours;
      values.oxygenation_dose_minutes = rowData?.oxygenation_dose_minutes;
    }
  }

  values.goal = rowData?.goal;
  values.note = rowData?.note;

  return values;
};

// Color palette for application badges - carefully chosen for visual harmony and accessibility
const APPLICATION_COLORS = [
  { name: "blue", bg: "rgba(52, 152, 219, 0.15)", text: "#2980b9", border: "#3498db" },
  { name: "emerald", bg: "rgba(46, 204, 113, 0.15)", text: "#27ae60", border: "#2ecc71" },
  { name: "amber", bg: "rgba(241, 196, 15, 0.15)", text: "#d68910", border: "#f39c12" },
  { name: "rose", bg: "rgba(231, 76, 60, 0.15)", text: "#c0392b", border: "#e74c3c" },
  { name: "purple", bg: "rgba(155, 89, 182, 0.15)", text: "#8e44ad", border: "#9b59b6" },
  { name: "teal", bg: "rgba(26, 188, 156, 0.15)", text: "#16a085", border: "#1abc9c" },
  { name: "indigo", bg: "rgba(52, 73, 94, 0.15)", text: "#34495e", border: "#2c3e50" },
  { name: "orange", bg: "rgba(230, 126, 34, 0.15)", text: "#d35400", border: "#e67e22" },
];

/**
 * Get a consistent color for an application based on its ID
 * @param {number|string} appId - The application ID
 * @returns {Object} - { bg, text, border, name } color object
 */
const getApplicationColor = (appId) => {
  if (!appId) return APPLICATION_COLORS[0];
  const hash = String(appId).split("").reduce((acc, char) => {
    return acc + char.charCodeAt(0);
  }, 0);
  const index = hash % APPLICATION_COLORS.length;
  return APPLICATION_COLORS[index];
};

const computeAddOpTypeString = (operation, treatments, analysis, getLabel) => {
  if (operation?.treatments?.length > 1) {
    const firstOp = operation.treatments[0]?.operation;
    const allSameOperation = operation.treatments.every(t => t.operation === firstOp);
    if (!allSameOperation) {
      return `${getLabel(`operationType${operation.type}`)} (#${
        operation?.treatments?.length
      } ${getLabel("protocolTreatments")})`;
    }
  }
  const treatment = operation?.treatments?.[0] || {};
  const protocol_type = treatment?.protocol_type || null;
  const subtype =
    operation?.type === "ADD"
      ? null
      : protocol_type
        ? getLabel(`drugLabelGroup_${protocol_type}_One`)?.toLowerCase?.()
        : null;

  return `${getLabel(
    `operationType${
      operation?.type === "ADD" ? treatment?.operation : operation.type
    }`,
  )} ${subtype ? `(${subtype})` : ""}`;
};

const computeAdditionalTypeString = (
  operation,
  treatments,
  analysis,
  getLabel,
) => {
  if (
    operation?.subtype &&
    ["CUT", "DECANT", "RACKING", "DESTEMMING", "NEW_LOT", "BOTTLING"].includes(
      operation?.type,
    )
  ) {
    const subtype = operation?.subtype;

    return ` (${getLabel(`operationSubtype${subtype}`)?.toLowerCase?.()})`;
  }
  
  if (
    [
      "CUT",
      "DECANT",
      "RACKING",
      "OUILLAGE",
      "ANALYSIS",
      "TREATMENT",
      "DESTEMMING",
      "NEW_LOT",
      "BOTTLING",
      "STABILIZATION_STATIC",
      "STABILIZATION_DYNAMIC",
      "STABILIZATION_RESIN",
    ].includes(operation?.type)
  )
    return "";
  
  if (operation?.type === "FILTERING") {
    const filterType = operation?.treatments?.[0]?.type || null;
    if (filterType) return ` (${getLabel(`filterType_${filterType}One`)?.toLowerCase?.()})`;
    return "";
  }

  const protocolType = operation?.treatments?.[0]?.protocol_type || null;
  if (
    operation?.completion_date === null &&
    [
      "STABILIZATION_STATIC",
      "STABILIZATION_DYNAMIC",
      "STABILIZATION_RESIN",
    ].includes(operation?.type)
  ) {
  }
  if (
    operation?.type === "OXYGENATION" &&
    ["MICRO", "MACRO", "CUSTOM"].includes(protocolType)
  )
    return ` (${getLabel(
      `protocolOXYGENATION_${protocolType}`,
    )})`?.toLowerCase();
  if (
    operation?.type === "HOMOGENIZATION_MASS" &&
    (protocolType?.includes("TEMP") || protocolType?.includes("REASSEMBLY"))
  )
    return ` (${getLabel(
      `operationTypeHOMOGENIZATION_${protocolType.replace("HOMOGENIZATION_", "")}`,
    )})`?.toLowerCase();
  // TO DO: aggiungi cappello o altro in omogenizzazione temperatura

  return "";
};

const computeRecurrentString = (operation, treatments, analysis, getLabel) => {
  if (!["ANALYSIS", "TREATMENT"].includes(operation?.type)) return "";
  const arr = (operation?.type === "TREATMENT" ? treatments : analysis) || [];
  const protocolName =
    arr?.find((p) => p?.protocols_id === operation?.protocol_id)
      ?.protocols_name || "";
  const pName = protocolName?.length > 0 ? ` [${protocolName}]` : "";

  const customName = operation?.custom_name?.trim() || null;
  if (!operation?.repetitive) {
    if (customName) return ` #${customName}${pName}`;
    else return ` #${operation?.id}${pName}`;
  }

  const current = operation?.relative_id || 0;
  const total = operation?.total || 0;

  return current > 0 && total > 0
    ? ` #${
        customName || operation?.main_id || operation?.id
      } (${current}/${total})${pName}`
    : "";
};

const computeOperationRow = (row, treatments, analysis, getLabel) => {
  // TODO add warning when operation is not executable
  // isOperationExecutable = (row, isInvalidSrcTank, analysisState, analysis, [], {}, {});
  return row?.type === "ADD"
    ? computeAddOpTypeString(row, treatments, analysis, getLabel)
    : getLabel(`operationType${row?.type}`) +
        computeAdditionalTypeString(row, treatments, analysis, getLabel) +
        computeRecurrentString(row, treatments, analysis, getLabel);
};

/**
 * Compute total operation duration and add it to the start date.
 *
 * @param {Object} operation - The operation Object
 * @returns {Date} - The resulting end date
 */
const computeDynamicStabilizationEndDate = (operation) => {
  const startDate = operation?.start_date;

  if (!startDate) return null;

  const treatmentData = operation?.treatments?.[0] || {};

  // Ensure numeric values (default to 0)
  const iterations = Number(treatmentData?.iter_per_day) || 0;
  const pause = Number(treatmentData?.pause_time) || 0;
  const work = Number(treatmentData?.work_time) || 0;

  // Total time in hours and minutes
  const totalPauseHours = iterations * pause;
  const totalWorkMinutes = iterations * work;

  // Add to start date
  let endDate = new Date(startDate);
  endDate = addHours(endDate, totalPauseHours);
  endDate = addMinutes(endDate, totalWorkMinutes);

  return endDate;
};

const getStartDate = (start, isOperationStarted = false) => {
  const startDate = isOperationStarted ? start : new Date();
  return startDate;
};

const computeElapsedTime = (startDate, endDate, isOperationCompleted) => {
  let diffMs = endDate - startDate;
  const isNegative = diffMs < 0;
  diffMs = Math.abs(diffMs);

  const totalMinutes = Math.floor(diffMs / (1000 * 60));
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  const formatted = `${isOperationCompleted ? "" : isNegative ? "-" : "+"}${
    hours > 0 ? hours : ""
  }${hours > 0 ? "h:" : ""}${String(minutes).padStart(
    hours > 0 ? 2 : 1,
    "0",
  )}m`;

  return { hours, minutes, formatted, isNegative };
};

/**
 * Compute elapsed time between two dates (can be negative).
 * Returns both formatted string and a CSS class based on thresholds.
 *
 * @param {Date | string | number} start - Start date or timestamp
 * @param {Date | string | number} end - End date or timestamp
 * @param {Boolean} isOperationStarted - False when the op has to be started -> start_date = 1970
 * @param {Boolean} isOperationCompleted - False when the op has to be completed
 * @returns {{ formatted: string, className: string }}
 */
const getElapsedTimeWithClass = (
  start,
  end,
  isOperationStarted = false,
  isOperationCompleted = false,
) => {
  const startDate = getStartDate(start, isOperationStarted);
  const endDate = new Date(end);

  const { hours, minutes, formatted, isNegative } = computeElapsedTime(
    startDate,
    endDate,
    isOperationCompleted,
  );

  const WARNING_HOURS = 3;

  // --- Determine class ---
  let className = "";
  if (!isOperationCompleted) {
    // compute signed hours for threshold checks
    const signedHours = (endDate - startDate) / (1000 * 60 * 60);

    if (signedHours > 0) className = "expired";
    else if (signedHours >= -WARNING_HOURS) className = "warning";
  }

  // --- Determine custom formatted output ---
  let customFormatted;
  if (Math.abs(hours) >= 24) {
    // Convert to days + remaining hours
    const totalHours = Math.abs(hours);
    const days = Math.floor(totalHours / 24);
    const remainingHours = Math.floor(totalHours % 24);

    // Format: "Xd:Yh"
    customFormatted = `${days}d:${remainingHours}h`;
    if (isNegative) customFormatted = `-${customFormatted}`;
  } else {
    // Use original formatted output under 24h
    customFormatted = formatted;
  }

  return { formatted: customFormatted, className, hours, minutes };
};

const computeFlowRates = (liters, hours, minutes) => {
  const totalMinutes = hours * 60 + minutes;
  const totalHours = hours + minutes / 60;

  return {
    l_per_min: Math.round(liters / totalMinutes, 2),
    l_per_hour: Math.round(liters / totalHours, 2),
  };
};

const flattenDrugIngredients = (concs) => {
  if (!Array.isArray(concs)) return [];
  return concs.flatMap((c) => {
    if (!Array.isArray(c.ingredients) || c.ingredients.length === 0) return [];

    return c.ingredients.map((ingredient) => ({
      drug_id: c.drug_id,
      product_name: c.drug_name,
      ingredient_name: ingredient.substance,
      quantity: (c.quantity * ingredient.quantity) / 100,
      unit: c.unit,
      type: ingredient.type || null,
      functional: ingredient.functional || null,
      physical: ingredient.physical || null,
      origins: ingredient.origins || [],
    }));
  });
};

const normalizeString = (str) => str?.trim()?.toLowerCase(); // Normalize names

const aggregateDrugConcentrations = (
  isDrugConcentration,
  isAdjuvant,
  compositions = [],
) => {
  if (!Array.isArray(compositions)) return [];

  // 1️⃣ Filtro per tipo
  const filtered = compositions.filter((item) => {
    if (!isAdjuvant) return item.type === "ADDITIVE";
    return true;
  });

  // 2️⃣ Aggrega
  const aggregated = Object.values(
    filtered.reduce((acc, item) => {
      // Decidi la chiave di aggregazione
      const key = isDrugConcentration
        ? normalizeString(item.ingredient_name) // ingredienti separati
        : normalizeString(item.product_name); // somma per prodotto

      if (!acc[key]) {
        acc[key] = { ...item, quantity: item?.quantity || 0 };
      } else {
        acc[key].quantity += item.quantity;
      }

      return acc;
    }, {}),
  );

  return aggregated;
};

const computeRequiredNumericQty = (substance, totalVolumeLiters = 0) => {
  const unit_id = substance?.unit_id;
  const quantity = substance?.quantity;

  if (!unit_id || !quantity || !totalVolumeLiters) return 0;

  switch (unit_id) {
    case 1: // mg/L → kg
      return (totalVolumeLiters * quantity) / 1_000_000;

    case 2: // g/hL → kg
      return ((totalVolumeLiters / 100) * quantity) / 1000;

    case 6: // g/L → kg
      return (totalVolumeLiters * quantity) / 1000;

    case 7: // g/100Kg ≈ g/100L → kg
      return ((totalVolumeLiters / 100) * quantity) / 1000;

    case 4: // Kg
      return quantity;

    case 3: // L (liquid additives ≈ kg)
      return quantity;

    case 5: // Unit
      return quantity;

    default:
      return 0;
  }
};

const computeRequiredWarehouseQty = (warehouseUnit, requiredNumericQty = 0) => {
  if (!warehouseUnit) return 0;

  switch (warehouseUnit.value) {
    case "kg":
      return requiredNumericQty; // ✅ already kg

    case "l":
      return requiredNumericQty; // liters already

    case "unit":
      return requiredNumericQty;

    default:
      return 0;
  }
};

const getWarehouseUnit = (substance, getLabel) => {
  const unit_id = Number(substance?.unit_id);

  switch (unit_id) {
    case 1:
    case 2:
    case 4:
    case 6:
    case 7:
      return { value: "kg", label: getLabel("drugBatchUnit_Kg") };
    case 3:
      return { value: "l", label: getLabel("drugBatchUnit_L") };
    case 5:
      return { value: "unit", label: getLabel("drugBatchUnit_Unit") };
    default:
      return null;
  }
};

const groupBySubstanceId = (items = []) => {
  const map = new Map();
  items = items ?? [];
  
  for (const item of items) {
    const { substance_id, unit, unit_id, drug_batches = [] } = item;

    let warehouse_unit = null;

    if (!map.has(substance_id)) {
      warehouse_unit = [1, 2, 6, 7].includes(unit_id)
        ? "Kg"
        : unit_id === 3
          ? "L"
          : unit;

      map.set(substance_id, {
        substance_id,
        drug_batches: [],
      });
    }

    const isEnoughStock = true;
    const existingBatches = map.get(substance_id).drug_batches;
    const batchById = new Map(
      existingBatches.map((b) => [b.drug_batch_id, b]).filter(([k]) => k != null)
    );

    for (const b of drug_batches || []) {
      if (b?.drug_batch_id && batchById.has(b.drug_batch_id)) {
        // Stessa batch fisica (es. BOTTLING: stesso lotto su più dest_tank):
        // accumula la quantità invece di aggiungere una riga duplicata.
        batchById.get(b.drug_batch_id).quantity =
          (batchById.get(b.drug_batch_id).quantity || 0) + Number(b.quantity || 0);
      } else {
        const entry = {
          ...b,
          substance_id,
          is_enough_stock: isEnoughStock,
          label: b?.batch_number,
          value: b?.batch_number,
          drug_batch_id: b?.drug_batch_id,
          unit: warehouse_unit,
          warehouse_unit: warehouse_unit,
        };
        existingBatches.push(entry);
        if (b?.drug_batch_id) batchById.set(b.drug_batch_id, entry);
      }
    }
  }

  return Array.from(map.values());
};

const buildBatchField = (substance_id, batch, quantity, warehouseUnit) => ({
  value: batch.batch_number,
  label: batch.batch_number,
  batch_number: batch.batch_number,
  drug_batch_id: batch.drug_batch_id,
  quantity,
  substance_id: substance_id,
  expire_date: batch.expire_date,
  warehouse_unit: warehouseUnit?.label,
});

const isNotExpired = (item) => {
  if (!item.expire_date) return true;

  const expireDate = new Date(item.expire_date);
  expireDate.setHours(23, 59, 59, 999);

  return expireDate >= new Date();
};

const isExpired = (item) => {
  return !isNotExpired(item);
};

const assignDrugBatches = (
  substances,
  totalTreatmentVolume,
  automaticDrugBatches,
  getLabel,
  isExcludeBatchNumber = false,
  isModal = true,
  setValue = null,
) => {
  const assigments = {}; // ✅ Use object instead of array to avoid sparse array issues
  const substancesWithBatches =
    substances?.filter(
      (b) => Array.isArray(b?.batches) && b?.batches?.length > 0,
    ) || [];

  for (let i = 0; i < substancesWithBatches?.length; i++) {
    const drug = substancesWithBatches?.[i];
    const drugId = drug?.substance_id || drug?.id;

    const filteredBatches =
      (drug?.batches || [])
        ?.filter(isNotExpired)
        ?.filter((b) =>
          isExcludeBatchNumber
            ? b?.batch_number !== isExcludeBatchNumber
            : true,
        ) || [];
    const sortedBatches = filteredBatches
      .sort((a, b) => new Date(a.expire_date) - new Date(b.expire_date))
      ?.map((b) => {
        return { ...b, value: b?.batch_number, label: b?.batch_number };
      });

    const warehouseUnit = getWarehouseUnit(drug, getLabel);
    // const requiredNumericQty = computeRequiredWarehouseQty(warehouseUnit, requiredNumericQty);
    const requiredNumericQty = computeRequiredNumericQty(
      drug,
      totalTreatmentVolume,
    );
    let remaining = computeRequiredWarehouseQty(
      warehouseUnit,
      requiredNumericQty,
    );

    const allocation = sortedBatches
      .map((batch) => {
        if (remaining <= 0) return null;

        const used = Math.min(batch.stock, remaining);
        remaining -= used;

        return buildBatchField(drugId, batch, used, warehouseUnit);
      })
      .filter(Boolean);

    if (allocation.length) {
      if (remaining <= 0) allocation[0].is_enough_stock = true;
      if (!isModal) automaticDrugBatches.replace(allocation); // update drug_batches_tmp (useFieldArray) in modal
      assigments[drugId] = allocation;
    }
  }

  // drug_batches is a map/object { substance_id: [...] }, not an array
  // Use setValue to update it (not useFieldArray.replace which expects array)
  if (Object.keys(assigments).length && isModal && setValue) {
    setValue("drug_batches", assigments);
  }
};

// Rescale drug_batches already assigned MANUALLY by the user when the treatment
// volume/quantity changes, instead of re-running FEFO auto-assignment (which would
// silently swap out the specific lots the user picked). Keeps the same batches and
// scales their quantities to the new required amount; if a batch's stock can't
// absorb its scaled-up share, the shortfall is drawn from the next unused batch
// in FEFO order. Substances with no existing assignment are left untouched.
const rescaleDrugBatches = (
  substances,
  totalTreatmentVolume,
  getLabel,
  existingAssignments = {},
) => {
  const assignments = {};
  const substancesWithBatches =
    substances?.filter(
      (b) => Array.isArray(b?.batches) && b?.batches?.length > 0,
    ) || [];

  for (let i = 0; i < substancesWithBatches?.length; i++) {
    const drug = substancesWithBatches[i];
    const drugId = drug?.substance_id || drug?.id;

    const existing = existingAssignments?.[drugId];
    if (!Array.isArray(existing) || existing.length === 0) continue;

    const currentTotal = existing.reduce(
      (sum, b) => sum + (Number(b.quantity) || 0),
      0,
    );
    if (currentTotal <= 0) continue;

    const warehouseUnit = getWarehouseUnit(drug, getLabel);
    const requiredNumericQty = computeRequiredNumericQty(
      drug,
      totalTreatmentVolume,
    );
    const requiredQty = computeRequiredWarehouseQty(
      warehouseUnit,
      requiredNumericQty,
    );

    const stockByBatchId = new Map(
      (drug.batches || []).map((b) => [b.drug_batch_id, b]),
    );
    const ratio = requiredQty / currentTotal;

    let shortfall = 0;
    const rescaled = existing.map((b) => {
      const stock = stockByBatchId.get(b.drug_batch_id)?.stock ?? Infinity;
      const desired = (Number(b.quantity) || 0) * ratio;
      const used = Math.min(desired, stock);
      if (desired > used) shortfall += desired - used;
      return { ...b, quantity: used };
    });

    if (shortfall > 0) {
      const usedIds = new Set(existing.map((b) => b.drug_batch_id));
      const additionalBatches = (drug.batches || [])
        .filter((b) => !usedIds.has(b.drug_batch_id))
        .filter(isNotExpired)
        .sort((a, b) => new Date(a.expire_date) - new Date(b.expire_date));

      for (const batch of additionalBatches) {
        if (shortfall <= 0) break;
        const used = Math.min(batch.stock, shortfall);
        shortfall -= used;
        rescaled.push(buildBatchField(drugId, batch, used, warehouseUnit));
      }
    }

    const finalAllocation = rescaled.filter((b) => Number(b.quantity) > 0);
    if (finalAllocation.length) {
      finalAllocation[0].is_enough_stock = shortfall <= 0;
      assignments[drugId] = finalAllocation;
    }
  }

  return assignments;
};

// Pure function: compute drug batch allocations without side effects on RHF form state
// Returns object { [substance_id]: [...batchFields] } ready for treatments_data
const computeDrugBatchAllocations = (
  substances,
  totalTreatmentVolume,
  getLabel,
  isExcludeBatchNumber = false,
) => {
  const assignments = {}; // Object keyed by substance_id
  const substancesWithBatches =
    substances?.filter(
      (b) => Array.isArray(b?.batches) && b?.batches?.length > 0,
    ) || [];
      
  for (let i = 0; i < substancesWithBatches?.length; i++) {
    const drug = substancesWithBatches?.[i];
    const drugId = drug?.substance_id || drug?.id;

    const filteredBatches =
      (drug?.batches || [])
        ?.filter(isNotExpired)
        ?.filter((b) =>
          isExcludeBatchNumber
            ? b?.batch_number !== isExcludeBatchNumber
            : true,
        ) || [];

    const sortedBatches = filteredBatches
      .sort((a, b) => new Date(a.expire_date) - new Date(b.expire_date))
      ?.map((b) => {
        return { ...b, value: b?.batch_number, label: b?.batch_number };
      });

    const warehouseUnit = getWarehouseUnit(drug, getLabel);
    const requiredNumericQty = computeRequiredNumericQty(
      drug,
      totalTreatmentVolume,
    );
    let remaining = computeRequiredWarehouseQty(
      warehouseUnit,
      requiredNumericQty,
    );

    const allocation = sortedBatches
      .map((batch) => {
        if (remaining <= 0) return null;

        const used = Math.min(batch.stock, remaining);
        remaining -= used;

        return buildBatchField(drugId, batch, used, warehouseUnit);
      })
      .filter(Boolean);

    if (allocation.length) {
      if (remaining <= 0) allocation[0].is_enough_stock = true;
      assignments[drugId] = allocation;
    }
  }

  return assignments;
};

// Helper: add or update a treatment row
const upsertTreatment = (tmp_treatments_data, tmp) => {
  const index = tmp_treatments_data.findIndex(
    (t) => t.substance_id === tmp.substance_id,
  );
  if (index > -1) tmp_treatments_data[index] = tmp;
  else tmp_treatments_data.push(tmp);
};

// Helper: resolve a clean numeric substance id from a table row.
// `product` can be the drug OBJECT (DrugsBatchSelectorTable rows): use its `id`,
// never Number({}) which yields NaN. Falls back to `value` (set to the substance id
// in the protocol/winemaking branches) and finally `analysis_id`.
const resolveRowSubstanceId = (rowOriginal) => {
  const p = rowOriginal?.product;
  const raw =
    rowOriginal?.substance_id ??
    (p && typeof p === "object" ? p?.id : p) ??
    rowOriginal?.value ??
    rowOriginal?.analysis_id;
  return Number(raw);
};

const buildTreatmentsDataArray = (
  allSubstances = [],
  drug_batches = [],
  data,
  protocols,
  protocol_id,
  type,
  id,
  operation,
  operations,
  analysisType,
) => {
  const treatments_data = [];
  const tmp_treatments_data = [];

  const removedSubIds = [];
  const addedSubIds = [];

  const getSubstanceById = (id) =>
    allSubstances?.find((s) => s?.id === Number(id));

  const isTrue = (val) => val?.value === true || val === true;
  const isFalse = (val) => val?.value === false || val === false;

  // Initialize removed and added substances
  if (typeof data?.removed === "object") {
    for (const [substance_id, isRemoved] of Object.entries(data.removed)) {
      const id = Number(substance_id);
      if (isTrue(isRemoved)) removedSubIds.push(id);
      else if (isFalse(isRemoved)) addedSubIds.push(id);
    }
  }

  const protocolToUse = protocols?.find(
    (p) =>
      p?.protocols_id === (id ? operation?.protocol_id : protocol_id?.value),
  );
  const protocolSubstances =
    protocolToUse?.[type === "TREATMENT" ? "drugs" : "analysis"] || [];

  // CREATE
  if (!id) {
    for (const s of protocolSubstances) {
      const substance = getSubstanceById(s.substance_id);
      if (!substance) continue;

      const tmp = {
        substance_id: substance.id,
        quantity: Number(s.quantity),
        unit_id: substance.unit_id,
        removed: removedSubIds.includes(substance.id),
      };
      if (!tmp.removed && addedSubIds.includes(substance.id)) tmp.added = true;

      upsertTreatment(tmp_treatments_data, tmp);
    }
  } else {
    // UPDATE
    const existingOp = operations?.find((o) => o.id === Number(id));

    for (const t of existingOp?.treatments || []) {
      if (!addedSubIds.includes(t.substance_id))
        addedSubIds.push(t.substance_id);
      if (
        !tmp_treatments_data.some((tt) => tt.substance_id === t.substance_id)
      ) {
        const tmp = { ...t, removed: removedSubIds.includes(t.substance_id) };
        delete tmp.tank_id;
        delete tmp.batch_id;
        upsertTreatment(tmp_treatments_data, tmp);
      }
    }
  }

  // Handle updated columns
  if (typeof data?.column === "object") {
    for (const [key, new_value] of Object.entries(data.column)) {
      if (new_value === null || new_value === undefined) continue;
      if (key === "tanks") continue; // analysis-on-tanks array, handled elsewhere

      // The quantity input registers under `column.<substanceId>_<rowKey>.value`
      // (rowKey = tank_id ?? batch_id ?? row index), while EditCell/manual writes
      // use `column.<substanceId>.{new,value}`. Resolve the substance id from either
      // key shape so the edited quantity is always picked up.
      const resolvedId =
        new_value?.id ?? new_value?.substance_id ?? String(key).split("_")[0];
      const substance = getSubstanceById(resolvedId);
      if (!substance) continue;
      const substance_id = substance?.substance_id || substance?.id;

      const existing = tmp_treatments_data.find(
        (t) => t.substance_id === substance_id,
      );

      // .new = user's typed value; .value = registered input value / original on edit-enter
      const rawVal = new_value?.new ?? new_value?.value;
      // An unparsable rawVal must never clobber an already-known quantity (e.g. the
      // protocol's own value) with the drug catalog's unrelated default `quantity` —
      // only fall back to that default when there is no existing quantity to preserve.
      const val =
        Number(rawVal) >= 0
          ? Number(rawVal)
          : existing
            ? existing.quantity
            : substance.quantity || 0;

      const tmp = {
        ...(existing || {}),
        substance_id,
        quantity: val,
        unit_id: substance.unit_id,
        unit: substance.unit,
        max_range: substance.max_range,
        min_range: substance.min_range,
        protocol_id: data?.protocol_id?.value,
        substance_name: substance.name,
        info: {
          analysisType: analysisType?.value,
          protocolFrequency: data?.protocolFrequency?.value,
        },
        ...(addedSubIds.includes(substance.id) && { added: true }),
        ...(removedSubIds.includes(substance.id) && { removed: true }),
      };

      if (existing) {
        upsertTreatment(tmp_treatments_data, tmp);
      } else if (
        substance_id &&
        (type === "ANALYSIS" || (type === "TREATMENT" && val > 0))
      ) {
        upsertTreatment(tmp_treatments_data, tmp);
      }
    }
  }

  // Handle drug batches (TREATMENT only)
  if (type === "TREATMENT" && drug_batches && typeof drug_batches === "object" && !Array.isArray(drug_batches)) {
    for (const treatment of tmp_treatments_data) {
      const drugBatchesPerSubstance =
        drug_batches?.[treatment.substance_id] || [];

      // if (drugBatchesPerSubstance === null) {
      //   toast.error(
      //     getLabel("toast_drugBatchInvalid", { name: treatment?.substance_name }),
      //     {
      //       toastId: customIdDrugBatches,
      //       position: toast.POSITION.BOTTOM_RIGHT,
      //       exclude: true,
      //       autoClose: 5000,
      //     }
      //   );
      //   return;
      // }

      if (
        addedSubIds.includes(treatment.substance_id) ||
        !protocolToUse?.substance_ids?.includes(treatment.substance_id)
      ) {
        treatment.added = true;
      }

      if (drugBatchesPerSubstance) {
        treatment.drug_batches = drugBatchesPerSubstance || [];
      }
    }
  }

  // Final assignment
  if (type === "TREATMENT") {
    treatments_data.push(...tmp_treatments_data);
  } else {
    treatments_data.push(
      ...tmp_treatments_data
        .filter((t) => t?.added || t?.removed === false)
        .map((t) => ({
          substance_id: t.substance_id,
          added: t.added || !!id,
        })),
    );
  }
  return treatments_data;
};

const getUsedDrugBatches = (treatments = []) => {
  const map = new Map();

  for (const treatment of treatments) {
    for (const batch of treatment.drug_batches || []) {
      const id = batch.drug_batch_id;
      if (!id) continue;
      if (!map.has(id)) {
        map.set(id, {
          drug_batch_id: id,
          drug_batch_number: batch.batch_number,
          unitary_cost: batch.unitary_cost,
          quantity: 0,
        });
      }

      map.get(id).quantity += Number(batch.quantity) || 0;
    }
  }

  return Array.from(map.values());
};

const groupDrugBatchesBySubstance = (treatments = [], substance_id = null) => {
  const result = {};

  for (const treatment of (treatments || [])?.filter((t) =>
    substance_id ? t?.substance_id === substance_id : true,
  )) {
    const substanceId = treatment?.substance_id;
    if (!substanceId) continue;

    if (!result[substanceId]) {
      result[substanceId] = [];
    }

    for (const batch of treatment.drug_batches || []) {
      result[substanceId].push({
        tank_id: batch.tank_id,
        quantity: Number(batch.quantity) || 0,
        drug_batch_number: batch.batch_number,
        unitary_cost: batch.unitary_cost,
        drug_batch_id: batch.drug_batch_id,
        warehouse_unit: [1, 2, 6, 7].includes(treatment?.unit_id)
          ? "Kg"
          : treatment?.unit_id === 3
            ? "L"
            : treatment?.unit,
      });
    }
  }

  return result;
};

const getUsedDrugBatchesBySubstance = (
  treatments = [],
  substance_id = null,
) => {
  const result = new Map();

  for (const treatment of (treatments || [])?.filter((t) =>
    substance_id ? t?.substance_id === substance_id : true,
  )) {
    const substanceId = treatment?.substance_id;
    if (!substanceId) continue;

    if (!result.has(substanceId)) {
      result.set(substanceId, new Map());
    }

    const batchMap = result.get(substanceId);

    for (const batch of treatment.drug_batches || []) {
      const batchId = batch?.drug_batch_id;
      if (!batchId) continue;

      if (!batchMap.has(batchId)) {
        batchMap.set(batchId, {
          drug_batch_id: batchId,
          batch_number: batch.batch_number,
          unitary_cost: batch.unitary_cost,
          quantity: 0,
        });
      }

      batchMap.get(batchId).quantity += Number(batch.quantity) || 0;
    }
  }

  // Convert Maps → plain objects
  return Object.fromEntries(
    Array.from(result.entries()).map(([substanceId, batchMap]) => [
      substanceId,
      Array.from(batchMap.values()),
    ]),
  );
};

// Interactive drug-batch column reused across detail tables (DESTEMMING/TREATMENT/AUTOMATIC/BOTTLING).
// Mirrors the working "batch_row" column used by SubstanceList: renders the +/pen button and inline lots
// via the TableCell "select" branch. Read once at build time: keep the host columns un-memoized so
// options2 refreshes after a BATCH_SELECTOR confirm (watchedDrugBatches re-render).
const buildDrugBatchRowColumn = (
  columnHelper,
  { control, getValues, drugs, configs, getLabel = () => null } = {},
) =>
  columnHelper.accessor("batch_row", {
    header: getLabel("drugBatchNumbers"),
    cell: TableCell,
    meta: {
      type: "select",
      control,
      editable: true,
      isFilterDisabled: true,
      dateFormat: configs?.shortDateFormat,
      options: drugs || [],
      options2: getValues?.("drug_batches") || [],
    },
  });

// Detail-only: when the current role can assign drug batches inline (USER cellarman, or CLIENT without
// any USER cellarmen) on a not-yet-completed operation.
const isDrugBatchInteractive = (role, auth, operation, users) =>
  operation?.completion_date === null &&
  ((Number(role) === ROLES.USER &&
    operation?.cellarman_ids?.includes(Number(auth?.user_id))) ||
    (Number(role) === ROLES.CLIENT &&
      users?.filter((u) => u?.type === "USER")?.length === 0));

const getTreatmentColumns = (
  type,
  auth,
  role,
  operation,
  isShowCost,
  register,
  users,
  watchedDrugBatches,
  getLabel = () => null,
  { control, getValues, drugs, configs, winery } = {},
) => {
  const columnHelper = createColumnHelper();
  const isInteractive = isDrugBatchInteractive(role, auth, operation, users);
  const currencySymbol = getCurrencySymbol(getCurrencyCode(winery));

  const columns =
    type === "CLEANING"
      ? [
          columnHelper.accessor(
            (row) => row?.day + (row?.day_to ? ` - ${row?.day_to}` : ""),
            {
              header: getLabel("protocolStep"),
              cell: TableCell,
              meta: {
                type: "number",
                register: register,
              },
            },
          ),
          ...(type !== "CLEANING"
            ? [
                columnHelper.accessor(
                  (row) =>
                    row?.phase ? getLabel(`protocolPhase_${row.phase}`) : "",
                  {
                    header: getLabel("protocolPhase"),
                    cell: TableCell,
                  },
                ),
              ]
            : []),
          columnHelper.accessor(
            (row) =>
              row?.operation
                ? getLabel(`protocolOperation_${row.operation}`)
                : "",
            {
              header: getLabel("protocolOperation"),
              cell: TableCell,
              meta: {
                type: "text",
                register: register,
              },
            },
          ),
          columnHelper.accessor(
            (row) =>
              row.operation === "RINSE" || row.operation === "VAPORIZATION"
                ? getLabel("protocolProduct_H2O")
                : row?.product_name || row?.substance_name || "",
            {
              header: getLabel("protocolProduct"),
              cell: TableCell,
              meta: {
                type: "text",
                register: register,
              },
            },
          ),
          columnHelper.accessor(
            (row) =>
              row?.quantity
                ? row.operation === "RINSE" || row.operation === "VAPORIZATION"
                  ? `${row?.quantity}`
                  : row.operation === "CLEANING"
                    ? `${row?.quantity} %`
                    : ""
                : "",
            {
              header: getLabel("protocolConcentration"),
              cell: TableCell,
              meta: {
                type: "number",
                register: register,
                isFilterDisabled: true,
              },
            },
          ),
          columnHelper.accessor(
            (row) =>
              row?.operation === "HOMOGENIZATION_MASS"
                ? ""
                : row?.work_time
                  ? `${row?.work_time} min`
                  : "",
            {
              header: getLabel("protocolHomogenizationContactTime"),
              cell: TableCell,
              meta: {
                type: "number",
              },
            },
          ),
          // ...(treatments?.operations?.find(
          //   (o) => o?.operation === "HOMOGENIZATION_MASS"
          // )
          //   ? [
          //       columnHelper.accessor(
          //         (row) =>
          //           row?.protocol_type
          //             ? getLabel(`protocolType_${row?.protocol_type}`) +
          //               (row?.protocol_type === "HOMOGENIZATION_REASSEMBLY" &&
          //               row?.homogenization_type
          //                 ? ` (${getLabel(
          //                     `protocolHomogenizationMass_${row?.homogenization_type}`
          //                   )})`
          //                 : "") +
          //               (row?.protocol_type === "CUSTOM"
          //                 ? ` (${row?.oxygenation_dose_hours}h:${row?.oxygenation_dose_minutes}m)`
          //                 : "")
          //             : "",
          //         {
          //           header: getLabel("protocolType"),
          //           cell: TableCell,
          //           meta: {
          //             type: "text",
          //           },
          //         }
          //       ),
          //       columnHelper.accessor(
          //         (row) =>
          //           row?.work_time &&
          //           (row?.pause_time || row?.homogenization_pause_time)
          //             ? `${row?.work_time}min / ${
          //                 row?.pause_time || row?.homogenization_pause_time
          //               }h  ${
          //                 type === "CLEANING" &&
          //                 row.operation === "HOMOGENIZATION_MASS" &&
          //                 row?.iter_per_day
          //                   ? `x ${row?.iter_per_day}`
          //                   : ""
          //               }`
          //             : "",
          //         {
          //           header: getLabel("protocolWorkPauseTime"),
          //           cell: TableCell,
          //           meta: {
          //             type: "number",
          //           },
          //         }
          //       ),
          //       columnHelper.accessor(
          //         (row) =>
          //           row?.homogenization_motor_speed
          //             ? row?.homogenization_motor_speed + "%"
          //             : "",
          //         {
          //           header: getLabel("protocolMotorSpeed"),
          //           cell: TableCell,
          //           meta: {
          //             type: "number",
          //           },
          //         }
          //       ),
          //     ]
          //   : []),
        ]
      : [
          columnHelper.accessor("substance_name", {
            header: getLabel("treatmentName"),
            cell: TableCell,
            meta: {
              isFilterDisabled: true,
            },
          }),
          columnHelper.accessor(
            (row) => `${row?.quantity} ${row?.unit || ""}`,
            {
              header: getLabel("quantity"),
              cell: TableCell,
              meta: {
                isFilterDisabled: true,
              },
            },
          ),
          // columnHelper.accessor("unit", {
          //   header: getLabel("unit"),
          //   cell: TableCell,
          // }),
          columnHelper.accessor(
            (row) =>
              row?.type ? getLabel(`drugType_${row.type?.toUpperCase()}`) : "",
            {
              header: getLabel("drugType"),
              cell: TableCell,
              meta: {
                type: "text",
                isFilterDisabled: true,
                filterVariant: "text",
              },
            },
          ),
          // columnHelper.accessor(
          //   (row) =>
          //     parseInt(row?.min_range) >= 0 && parseInt(row?.max_range) >= 0
          //       ? `${String(row?.min_range) || "0"} - ${
          //           String(row?.max_range) || ""
          //         } ${row?.unit}`
          //       : "",
          //   {
          //     header: getLabel("substanceRange"),
          //     cell: TableCell,
          //     meta: {
          //       type: "number",
          //       isFilterDisabled: true,
          //     },
          //   }
          // ),
          columnHelper.accessor(
            (row) =>
              `${
                row?.min_temperature ? String(row?.min_temperature) : "n/a"
              } - ${
                row?.max_temperature ? String(row?.max_temperature) : "n/a"
              } \u00b0C` || "",
            {
              header: getLabel("substanceTemperature"),
              cell: TableCell,
              meta: {
                type: "number",
                isFilterDisabled: true,
              },
            },
          ),
          // columnHelper.accessor("drug_batches", {
          //   header: getLabel("drugBatchNumber"),
          //   cell: MultipleRowsCell,
          //   meta: {
          //     array: "drug_batches",
          //     selector: "drug_batch_number",
          //     isFilterDisabled: true,
          //   },
          // }),
          isInteractive && !isShowCost
            ? buildDrugBatchRowColumn(columnHelper, {
                control,
                getValues,
                drugs,
                configs,
                getLabel,
              })
            : columnHelper.accessor("drug_batches", {
                header: getLabel("drugBatchNumbers"),
                cell: ({ row }) => {
                  const { substance_id, drug_batches: originalBatches } =
                    row.original;

                  const modifiedBatches = watchedDrugBatches?.[substance_id] || [];
                  const effectiveBatches =
                    (Array.isArray(modifiedBatches) && modifiedBatches?.length > 0
                      ? modifiedBatches
                      : originalBatches) || [];

                  // Creiamo un map per tracciare batch numbers unici e il loro stato di scadenza
                  const batchMap = new Map();
                  effectiveBatches.forEach((b) => {
                    const batchNumber = b.drug_batch_number || b.batch_number;
                    if (!batchMap.has(batchNumber)) {
                      batchMap.set(batchNumber, b);
                    }
                  });

                  return (
                    <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
                      {Array.from(batchMap.entries()).map(([batchNumber, batch]) => (
                        <span key={batchNumber} style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                          {batchNumber}
                          {isExpired(batch) && (
                            <span className="error_mark" title="Batch scaduto">
                              !
                            </span>
                          )}
                        </span>
                      ))}
                    </div>
                  );
                },
                meta: {
                  isFilterDisabled: true,
                },
              }),
        ];

  if (isShowCost) {
    columns.push(
      columnHelper.accessor(
        (row) =>
          `${
            (row?.drug_batches || [])?.length > 0
              ? (row?.drug_batches || [])
                  ?.reduce((acc, curr) => {
                    return (
                      acc +
                        Number(curr?.unitary_cost) *
                          Number(curr?.quantity) *
                          ([1, 2, 3, 6, 7].includes(row.unit_id) ? 1000 : 1) ||
                      0
                    );
                  }, 0)
                  .toFixed(2) || 0
              : "-"
          } ${(row?.drug_batches || [])?.length > 0 ? currencySymbol : ""}`,
        {
          header: getLabel("drugBatchProductCost"),
          cell: TableCell,
          meta: {
            isFilterDisabled: true,
          },
        },
      ),
    );
  }

  // Detail drug-batch editing is now handled by the interactive `batch_row` column above
  // (added when isInteractive). The legacy EditCell "drugBatchUpdate" column has been removed.

  return columns;
};

const initializeDrugBatches = (operation, drugs, setValue) => {
  const tmpSubstances = drugs?.map((s) => {
    return {
      ...s,
      removed:
        operation?.treatments?.find(
          (dis) =>
            dis?.substance_id == s?.substance_id && dis?.removed === true,
        ) !== undefined,
    };
  });
  tmpSubstances?.forEach((s, i) =>
    s?.removed ? setValue(`removed.${s?.substance_id}.value`, true) : null,
  );

  const uniqueTreatments = (operation?.treatments || [])?.map((s) => {
    return {
      substance_id: s?.substance_id,
      unit: s?.unit,
      unit_id: s?.unit_id,
      drug_batches:
        s?.drug_batches?.map((b) => {
          return {
            ...b,
            value: b?.batch_number,
            label: b?.batch_number,
          };
        }) || [],
    };
  });

  const groupedTreatmentsBySubstanceId = groupBySubstanceId(uniqueTreatments); // trick to use operation?.treatments
  for (const t of groupedTreatmentsBySubstanceId) {
    if ((t?.drug_batches || [])?.length > 0) {
      setValue(`drug_batches.${t?.substance_id}`, t?.drug_batches);
    }
  }
};

const initializeProtocolId = (type, protocols, protocol_id, setValue) => {
  if (protocol_id && Array.isArray(protocols)) {
    // BOTTLING protocols are passed in already shaped like TREATMENT protocols
    // (see buildWinemakingTreatment in BottlingForm.js), so both cases match the
    // same way: type "TREATMENT" + protocols_id.
    const chosenProtocol = protocols.find(
      (p) => p?.type === "TREATMENT" && p?.protocols_id === protocol_id,
    );

    if (chosenProtocol) {
      const p = {
        ...chosenProtocol,
        value: chosenProtocol.protocols_id,
        label: chosenProtocol.protocols_name,
        name: chosenProtocol.protocols_name,
      };
      setValue("protocol_id", p);
    }
  }
};

const aggregateDrugsById = (items) => {
  const map = new Map();

  for (const item of items) {
    const key = item.substance_id;

    if (!map.has(key)) {
      map.set(key, {
        ...item,
        transfer_details_id: [item.transfer_details_id],
        treatment_details_id: [item.treatment_details_id],
        drug_batches: [],
        total_quantity: 0,
        drug_batch_cost: 0,
      });
    }

    const acc = map.get(key);

    acc.transfer_details_id.push(item.transfer_details_id);
    acc.treatment_details_id.push(item.treatment_details_id);

    for (const batch of item.drug_batches || []) {
      // 🔢 quantità in kg già normalizzata a monte
      const qty = Number(batch.quantity || 0);
      const unitCost = Number(batch.unitary_cost || 0) * 1000;

      // 🔁 merge per drug_batch_id
      let accBatch = acc.drug_batches.find(
        (b) => b.drug_batch_id === batch.drug_batch_id,
      );

      if (!accBatch) {
        accBatch = {
          ...batch,
          quantity: qty, // kg
        };
        acc.drug_batches.push(accBatch);
      } else {
        accBatch.quantity += qty;
      }

      // 💰 costo incrementale
      acc.total_quantity += qty;
      acc.drug_batch_cost += qty * unitCost;
    }
  }

  // arrotondamenti finali
  const result = Array.from(map.values()).map((item) => ({
    ...item,
    total_quantity: Number(item.total_quantity.toFixed(6)),
    drug_batch_cost:
      Number(item.drug_batch_cost.toFixed(3)) > 0 &&
      Number(item.drug_batch_cost.toFixed(2) < 0.01)
        ? "≈0.00"
        : Number(item.drug_batch_cost.toFixed(2)),
  }));

  // sort only if "day" is defined
  if (result.every((r) => r.day != null)) {
    result.sort((a, b) => Number(a.day) - Number(b.day));
  }

  return result;
};

const customIdResendActivation = "custom-id-resend-activation";
const resendActivationEmail = async (e, email, getLabel, axiosPrivate) => {
  e.stopPropagation();
  if (!email) return;

  console.log("Resend e-mail...");

  const response = await toastAxios(
    () => resendNewClientEmail(email, axiosPrivate),
    customIdResendActivation,
    null,
    getLabel,
  );
};

const computeModifiedDrugBatches = (
  row_drug_batches = [],
  drug_batches = [],
) => {
  // used when the cellarman changes the drug batches
  // if (row_drug_batches?.length)

  return row_drug_batches;
};

const isSrcTanksStillInvalid = (operation, lots = [], tanks = []) => {
  if (!Array.isArray(operation?.src_tanks) || operation?.src_tanks?.length === 0 || operation?.completion_date || lots?.length === 0 || tanks?.length === 0) {
    return;
  }
  
  let isSrcTankError = false;
  operation?.src_tanks?.forEach((t) => {
    if (["DESTEMMING"].includes(operation?.type)) {
      // CRYO_MACERATION started: GRAPE batch was intentionally transferred → source is expected empty
      if (operation?.subtype === "CRYO_MACERATION" && operation?.start_date) return false;
      const lot = lots?.find((ll) => ll?.id === t?.batch_id);
      if (lot === undefined || lot?.actual_quantity <= 0) {
        isSrcTankError = true;
        return false;
      }
    } else if (["NEW_LOT"].includes(operation?.type)) {
      // NEW_LOT ha un src_tank fittizio (acquisto diretto di MUST/WINE), non va validato
      return false;
    } else if (operation?.type === "ANALYSIS" && operation?.on_tank === false) {
      for (const s of operation?.src_tanks || []) {
        const lot = lots?.find(l => l?.id == s?.batch_id);
        if (lot === undefined || !lot?.quantity) {
          isSrcTankError = true;
          return false;
        }
      }
    } else {
      const tank = tanks?.find((tt) => tt?.id === Number(t?.tank_id));
      if (tanks?.length > 0 && (tank === undefined || tank?.quantity <= 0 || !tank?.batch_name)) {
        isSrcTankError = true;
        return false;
      }
    }
  }); 
        
  return isSrcTankError;
  // if (isSrcTankError) {
  //   return true;
  // } else {
  //     toast.dismiss(customIdInvalidSrcTank);
  //     return false;
  // }
}

const isOperationExecutable = (operation, isInvalidSrcTank, analysisState, analysis = [], completed = [], drop = [], column = {}, errors = {}, isNextToExecute = true, hasAnalysisSampled = false) => {
  // Check if operation is part of a protocol application and not the next step to execute
  const isProtocolOperation = !!operation?.protocol_application_id;
  if (isProtocolOperation && !isNextToExecute && !hasAnalysisSampled) {
    return { disabled: true, tooltipMessage: "errorOperationNotNextToExecute" };
  }
  const repetitiveDisabled =
    operation?.repetitive &&
    Number(operation?.completed) < Number(operation?.relative_id) - 1;
  const checkboxDisabled =
    operation?.type === "BOTTLING"
      ? completed?.[operation?.dest_tanks?.[0]?.drop_batch_id] === false
      : Array.isArray(completed)
        ? !completed?.every((v) => v === true)
        : true;
  const dropDisabled =
    operation?.type === "DESTEMMING" &&
    !(operation?.subtype === "CRYO_MACERATION" && !operation?.start_date) &&
    (drop?.filter((c) => c) || [])?.length <
      operation?.dest_tanks?.length;
  const analysisNum = operation?.treatments?.length || 1;
    // analysisState === "INSERTING"
    //   ? analysis?.find((a) => a.protocols_id == operation?.protocol_id)
    //       ?.analysis?.length || 0
    //   : 0;

  const repetitiveIterationDisabled = (() => {
    if (analysisState !== "INSERTING" || !operation?.repetitive) return false;

    // For ANALYSIS operations with repetitive, check column.tanks.
    // operation.treatments holds only the current iteration's substance/tank pairs.
    // Some substances (e.g. Turbidity) have multiple treatment_details rows with
    // different substance_id for different color/state ranges (RED vs WHITE/ROSE/
    // ORANGE), but they're the SAME logical substance and every tank needs exactly
    // one result for it — the UI already merges those rows into a single input per
    // (substance_name, tank) (see the merge in OperationsDetail/index.js). Dedup by
    // substance_id here would double-count them, so key on substance_name instead.
    if (operation?.type === "ANALYSIS") {

      const requiredCount =
        new Set(
          (operation?.treatments || [])
            .filter((t) => !t?.removed)
            .map((t) => `${t?.substance_name}_${t?.tank_id ?? t?.batch_id}`)
        ).size || 1;
      const filledCount = (column?.tanks?.filter((c) => c?.value !== undefined && c?.value !== null && c?.value !== "") || []).length;
      // errors.column is an object keyed by "<substance_id>_<tank_id|batch_id|index>"
      // (see TableCell.js's registered field name), each holding a `.value` RHF error
      // (e.g. "required" when a filled-in result is cleared) — NOT an array, so the old
      // Array.isArray(errors?.column) check was always false and never actually blocked.
      const hasErrors =
        errors?.column && typeof errors.column === "object"
          ? Object.values(errors.column).some((c) => c?.value)
          : false;
      return filledCount < requiredCount || hasErrors;
    }

    // For other operations
    const filledCount = (Array.isArray(column) ? column.filter((c) => c?.value) : []).length;
    const hasErrors = (Array.isArray(errors?.column) ? errors.column.filter((c) => c?.value) : []).length > 0;
    return filledCount < analysisNum || hasErrors;
  })();

  const isCompleted = operation?.completion_date !== null;
  // TO DO: campionamento e dopo analisi tutto in fila, ho il pulsante disabilitato
  const disabled =
    isCompleted ||
    repetitiveDisabled ||
    checkboxDisabled ||
    dropDisabled ||
    repetitiveIterationDisabled;

  const tooltipMessage =
    isInvalidSrcTank ? "errorSrcTankNowEmpty"
    : isCompleted
      ? "errorOpsCompleted"
      : repetitiveDisabled
      ? "errorPreviousRecurrOpsNotCompleted"
      : checkboxDisabled
      ? "errorNotAllCheckboxCompleted"
      : repetitiveIterationDisabled
      ? "errorNotAllAnalysisCompleted"
      : ""
  return { disabled, tooltipMessage };
};

const isAnalysisExecutableOrSample = (
  operation,
  analysisResults = {},
  lots = [],
  tanks = [],
  isNextToExecute = true
) => {
  // Check if operation is part of a protocol application and not the next step to execute
  // For ANALYSIS, the sample taking is blocked if not the next step
  const isProtocolOperation = !!operation?.protocol_application_id;
  if (isProtocolOperation && !isNextToExecute) {
    return true; // disabled = true
  }

  // Sampling order only depends on how many prior iterations were SAMPLED
  // (exams.collection_date), not on how many had RESULTS entered (completed,
  // operations.completion_date) -- those are two separate constraints.
  const isCurrentIterationDisabled =
    Number(operation?.relative_id) - Number(operation?.sampled_count) > 1;
  const isSomeLotAlredyProcessed = // ANALYSIS on a batch no more existing (already DESTEMMED)
    operation?.on_tank === false &&
    operation?.src_tanks?.some((s) => !lots?.some((l) => l?.id == s?.batch_id));
  const isSomeTankAlredyProcessed = // ANALYSIS on a batch no more existing (already DESTEMMED)
    operation?.on_tank === true && // ANALYSIS on tank no more containing expected data (already processed)
    operation?.src_tanks?.some(
      (s) =>
        !tanks?.some(
          (t) =>
            t?.id == s?.tank_id &&
            t?.batch_id == s?.batch_id &&
            t?.quantity > 0,
        ),
    );

  // For repetitive operations, skip the analysisResults length check
  // since results from previous cycles are already saved in DB
  const analysisResultsCheck = !operation?.repetitive &&
    analysisResults?.length <
      Number(operation?.completed) * operation?.src_tanks?.length &&
      (!analysisResults?.results || analysisResults?.results?.length === 0);

  const disabled =
    isCurrentIterationDisabled ||
    analysisResultsCheck ||
    isSomeLotAlredyProcessed ||
    isSomeTankAlredyProcessed;

  if (disabled) {
    console.log('isAnalysisExecutableOrSample disabled:', {
      isCurrentIterationDisabled,
      analysisResultsCheck: analysisResults?.length < Number(operation?.completed) * operation?.src_tanks?.length && (!analysisResults?.results || analysisResults?.results?.length === 0),
      isSomeLotAlredyProcessed,
      isSomeTankAlredyProcessed
    });
  }

  return disabled;
};

/**
 * Apply wine color transformation based on operation subtype.
 * Handles color changes for maceration, orange wine, and salasso operations.
 *
 * @param {string} color - Initial wine color (e.g. "RED", "WHITE")
 * @param {string} subtype - Operation subtype (e.g. "CARBONIC_MACERATION")
 * @returns {string} Transformed color
 */
const applySubtypeColorTransformation = (color, subtype) => {
  if (!color || !subtype) return color;
  if (["CARBONIC_MACERATION", "ROSE_MACERATION", "DIRECT_PRESSING"].includes(subtype) && color === "RED") return "ROSE";
  if (subtype === "ORANGE_WINE" && color === "WHITE") return "ORANGE";
  if (subtype === "SALASSO" && color === "RED") return "ROSE";
  return color;
};

/**
 * Check if a tank is sanitized based on previous CLEANING/CIP steps
 * A tank is considered sanitized if:
 * 1. It was cleaned in a previous CLEANING or WINEMAKING (CIP) step, OR
 * 2. It was sanitized in a previous step and now remains empty
 *
 * @param {string|number} tankId - The tank ID to check
 * @param {number} stepIdx - Current step index
 * @param {Array} allSteps - All protocol steps
 * @param {Array} allStepConfigs - All step configurations
 * @param {Array} virtualTanks - List of all virtual tanks
 * @returns {boolean} True if tank is sanitized
 */
const checkTankSanitization = (tankId, stepIdx, allSteps, allStepConfigs, virtualTanks) => {
  if (!allSteps || !allStepConfigs || !virtualTanks) return false;

  const tankIdStr = String(tankId);

  // Check if tank was cleaned in a previous CLEANING step
  const wasCleanedInPreviousStep = allSteps?.some((step, idx) => {
    if (idx >= stepIdx) return false; // Only check previous steps
    const config = allStepConfigs?.[idx];
    if (!config) return false;

    // Only CLEANING steps can sanitize tanks
    if (step?.operation?.toUpperCase?.() !== "CLEANING") return false;

    // Check if this specific tank was targeted by the CLEANING step (via src_tanks)
    if (config?.src_tanks?.length > 0) {
      const tankInCleaning = config.src_tanks.some(
        (t) => String(t?.tank_id) === tankIdStr || String(t?.value) === tankIdStr
      );
      if (!tankInCleaning) return false;
    } else if (
      config?.winemaking_protocol_id || config?.working_protocol_id || config?.sub_winemaking_protocol_id
    ) {
      // Fallback: CLEANING with a protocol ID but no src_tanks — treat all empty tanks as cleaned
    } else {
      return false;
    }

    // Tank is sanitized only if it is still empty
    const tank = virtualTanks?.find(t => t.id === tankId);
    return Number(tank?.quantity) === 0;
  });

  // Check if tank was explicitly marked as sanitized in a previous step's src_tanks
  const wasSanitizedInPreviousStep = allSteps?.some((s, idx) => {
    if (idx >= stepIdx) return false; // Only check previous steps
    const config = allStepConfigs?.[idx];
    if (!config?.src_tanks) return false;

    const tankInConfig = config.src_tanks?.find(
      (dt) => String(dt?.tank_id) === tankIdStr || String(dt?.value) === tankIdStr
    );
    if (!tankInConfig) return false;

    return tankInConfig?.is_sanitized === true;
  });

  // Tank is sanitized if:
  // 1. It was specifically cleaned in a CLEANING step and is still empty, OR
  // 2. It was explicitly marked sanitized in a previous step's src_tanks and is still empty
  const tank = virtualTanks?.find(t => t.id === tankId);
  return wasCleanedInPreviousStep || (wasSanitizedInPreviousStep && Number(tank?.quantity) === 0);
};

/**
 * Enhance tanks with is_sanitized property based on previous CLEANING/CIP steps
 *
 * @param {Array} tanks - List of tanks to enhance
 * @param {number} stepIdx - Current step index
 * @param {Array} allSteps - All protocol steps
 * @param {Array} allStepConfigs - All step configurations
 * @param {Array} virtualTanks - List of all virtual tanks
 * @returns {Array} Enhanced tanks with is_sanitized property
 */
const enhanceTanksWithSanitization = (tanks, stepIdx, allSteps, allStepConfigs, virtualTanks) => {
  if (!tanks || !Array.isArray(tanks)) return tanks;

  return tanks.map((tank) => {
    const isSanitized = checkTankSanitization(tank.id, stepIdx, allSteps, allStepConfigs, virtualTanks);
    return isSanitized ? { ...tank, is_sanitized: true } : tank;
  });
};

/**
 * Get timing info for started operations
 * Calculates elapsed and remaining time based on operation type
 *
 * @param {Object} operation - Operation object with start_date and treatments
 * @returns {{elapsed: string | null, remaining: string | null, endDate: Date | null, elapsedClass: string}} Timing info
 */
const getStartedOperationTimingInfo = (operation) => {
  if (!operation?.start_date) {
    return {
      elapsed: null,
      remaining: null,
      endDate: null,
      elapsedClass: "",
    };
  }

  const operationType = operation.type;
  const treatment = operation.treatments?.[0];
  const subtype = treatment?.protocol_type; // For HOMOGENIZATION_MASS

  // Elapsed-only types: FILTERING, OXYGENATION, STABILIZATION_RESIN, HOMOGENIZATION_TEMP
  if (
    ["FILTERING", "OXYGENATION", "STABILIZATION_RESIN"].includes(operationType) ||
    (operationType === "HOMOGENIZATION_MASS" && subtype?.includes("TEMP"))
  ) {
    const elapsedInfo = getElapsedTimeWithClass(
      new Date(operation.start_date),
      new Date(),
      true,
      false
    );
    // For types without a target duration, don't show expired class
    const elapsedClass = ["FILTERING", "OXYGENATION", "HOMOGENIZATION_TEMP"].includes(operationType) ? "" : elapsedInfo.className;

    return {
      elapsed: elapsedInfo.formatted,
      remaining: null,
      endDate: null,
      elapsedClass,
    };
  }

  // Elapsed + remaining types
  let endDate = null;

  if (operationType === "STABILIZATION_STATIC") {
    // work_time is in days
    const workTime = Number(treatment?.work_time) || 0;
    endDate = addHours(new Date(operation.start_date), workTime * 24);
  } else if (
    operationType === "STABILIZATION_DYNAMIC" ||
    (operationType === "HOMOGENIZATION_MASS" && subtype?.includes("REASSEMBLY"))
  ) {
    // Uses iter_per_day, pause_time (hours), work_time (minutes)
    endDate = computeDynamicStabilizationEndDate(operation);
  } else if (operationType === "DESTEMMING" && operation?.subtype === "CRYO_MACERATION") {
    // work_time in treatment_details is in hours for cryo; fallback to metadata.hours
    const cryoHours = Number(treatment?.work_time) || Number(operation?.metadata?.hours) || 0;
    if (cryoHours > 0) endDate = addHours(new Date(operation.start_date), cryoHours);
  }

  if (!endDate) {
    return {
      elapsed: null,
      remaining: null,
      endDate: null,
      elapsedClass: "",
    };
  }

  const elapsedInfo = getElapsedTimeWithClass(
    new Date(operation.start_date),
    new Date(),
    true,
    false
  );
  const remainingInfo = getElapsedTimeWithClass(
    endDate,
    new Date(),
    true,
    false
  );

  return {
    elapsed: elapsedInfo.formatted,
    remaining: remainingInfo.formatted,
    endDate,
    elapsedClass: remainingInfo.className,
  };
};

/**
 * Get treatment timing display based on timing_mode
 * Returns formatted date range and appropriate labels
 *
 * @param {Object} operation - Operation object with treatments array
 * @param {Object} configs - Config object with dateFormat
 * @param {Function} getLabel - Function to get localized labels
 * @returns {{dateRange: string, fieldLabel: string, sublabel: string, isTimed: boolean, isOnce: boolean}} Timing display info
 */
const getTreatmentTimingDisplay = (operation, configs, getLabel) => {
  const defaultFormat = (date) =>
    format(new Date(date), configs?.shortDateNoYearFormat || "dd MMM");

  // Check if operation has treatments with timing_mode
  if (!operation?.treatments || operation.treatments.length === 0) {
    return {
      dateRange: defaultFormat(operation?.expire_date),
      fieldLabel: getLabel("expiringDate"),
      sublabel: "",
      isTimed: false,
      isOnce: false,
    };
  }

  const treatment = operation.treatments[0];
  const timingMode = treatment?.timing_mode;
  const expireDate = new Date(treatment?.expire_date || operation?.expire_date);

  if (!timingMode || timingMode === "DEFAULT") {
    return {
      dateRange: defaultFormat(operation?.expire_date),
      fieldLabel: getLabel("expiringDate"),
      sublabel: "",
      isTimed: false,
      isOnce: false,
    };
  }

  const dayFrom = Number(treatment?.day_from) || 0;
  const dayTo = Number(treatment?.day_to) || 0;

  if (timingMode === "RANGE") {
    const startDate = addDays(expireDate, dayFrom);
    const endDate = addDays(expireDate, dayTo);
    return {
      dateRange: `${defaultFormat(startDate)} - ${defaultFormat(endDate)}`,
      fieldLabel: getLabel("operationExecutionRange"),
      sublabel: getLabel("operationTimingRepetitiveRange"),
      isTimed: true,
      isOnce: false,
    };
  }

  if (timingMode === "SPECIFIC") {
    return {
      dateRange: defaultFormat(expireDate),
      fieldLabel: getLabel("toDoDate"),
      sublabel: "", //getLabel("operationTimingSpecific") || "Data specifica",
      isTimed: true,
      isOnce: true,
    };
  }

  if (timingMode === "ONCE_WITHIN") {
    const startDate = addDays(expireDate, dayFrom);
    const endDate = addDays(expireDate, dayTo);
    return {
      dateRange: `${defaultFormat(startDate)} - ${defaultFormat(endDate)}`,
      fieldLabel: getLabel("operationExecutionRange"),
      sublabel: getLabel("operationTimingOnceWithin"),
      isTimed: true,
      isOnce: true,
    };
  }

  return {
    dateRange: defaultFormat(operation?.expire_date),
    fieldLabel: getLabel("expiringDate"),
    sublabel: "",
    isTimed: false,
    isOnce: false,
  };
};

/**
 * Get color by type (sensor, label, etc)
 * Centralizes non-wine color mappings to avoid duplication
 *
 * @param {string} type - Color type identifier (TEMPERATURE, HUMIDITY, PRESSURE, etc)
 * @param {string} colorCategory - Category of color: 'sensor', 'label', 'default'
 * @returns {string} Color in hex format
 */
const getColorByType = (type, colorCategory = "sensor") => {
  const colorMaps = {
    sensor: {
      TEMPERATURE: "#64242E",
      HUMIDITY: "#627FE1",
      PRESSURE: "#9D7E68",
    },
    label: {
      Bio: "light_green",
      Vegan: "green",
    },
  };

  const categoryMap = colorMaps[colorCategory] || colorMaps.sensor;
  return categoryMap[type] || "#64242E";
};

export {
  isNotExpired,
  isExpired,
  assignDrugBatches,
  rescaleDrugBatches,
  computeDrugBatchAllocations,
  groupBySubstanceId,
  getWarehouseUnit,
  computeRequiredNumericQty,
  computeRequiredWarehouseQty,
  aggregateDrugConcentrations,
  flattenDrugIngredients,
  computeDynamicStabilizationEndDate,
  computeOperationRow,
  getElapsedTimeWithClass,
  getStartDate,
  computeElapsedTime,
  computeFlowRates,
  buildTreatmentsDataArray,
  resolveRowSubstanceId,
  getUsedDrugBatchesBySubstance,
  groupDrugBatchesBySubstance,
  getUsedDrugBatches,
  getTreatmentColumns,
  buildDrugBatchRowColumn,
  isDrugBatchInteractive,
  initializeDrugBatches,
  initializeProtocolId,
  aggregateDrugsById,
  resendActivationEmail,
  computeModifiedDrugBatches,
  isSrcTanksStillInvalid,
  isOperationExecutable,
  isAnalysisExecutableOrSample,
  getWineColor,
  getWineColorClassName,
  applySubtypeColorTransformation,
  checkTankSanitization,
  enhanceTanksWithSanitization,
  getStartedOperationTimingInfo,
  getTreatmentTimingDisplay,
  getColorByType,
  getApplicationColor,
  getOperationUrlSegment,
  toBackendOperationType,
  buildWineMakingModalValues,
  ADDITIVE_OP_TYPES,
};
